import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { ChangePctCell } from "./ChangePctCell";
import { FavoriteStar } from "./FavoriteStar";
import { Input } from "./ui/input";
import { cn } from "./ui/utils";
import type { IpListItem } from "@/types";

const columnHelper = createColumnHelper<IpListItem>();

export function IpTable({ data, favorites }: { data: IpListItem[]; favorites: string[] }) {
  const navigate = useNavigate();
  const favSet = useMemo(() => new Set(favorites), [favorites]);
  // 既定では sort なし — sortedData が favorites-first + 元順を維持する。
  // ヘッダクリックでユーザーが任意のカラムで sort できる。
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // ★行を上に固定するため、表示前に並べ替え
  const sortedData = useMemo(() => {
    const favs: IpListItem[] = [];
    const rest: IpListItem[] = [];
    for (const ip of data) (favSet.has(ip.ip_id) ? favs : rest).push(ip);
    return [...favs, ...rest];
  }, [data, favSet]);

  const filtered = useMemo(() => {
    return sortedData.filter((ip) => {
      if (favoritesOnly && !favSet.has(ip.ip_id)) return false;
      if (categoryFilter && ip.category !== categoryFilter) return false;
      if (globalFilter && !ip.display_name.toLowerCase().includes(globalFilter.toLowerCase())) return false;
      return true;
    });
  }, [sortedData, favSet, favoritesOnly, categoryFilter, globalFilter]);

  const categories = useMemo(
    () => Array.from(new Set(data.map((d) => d.category).filter((c): c is string => !!c))),
    [data]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("display_name", {
        header: "IP 名",
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }),
      columnHelper.accessor("category", {
        header: "カテゴリ",
        cell: (info) => <span className="text-xs text-muted-foreground">{info.getValue() ?? "-"}</span>,
      }),
      columnHelper.accessor("latest_total_score", {
        header: "総合",
        cell: (info) => {
          const v = info.getValue();
          return <span className="tabular-nums">{v?.toFixed(1) ?? "-"}</span>;
        },
      }),
      columnHelper.accessor("weekly_change_pct", {
        header: "前週比",
        cell: (info) => <ChangePctCell value={info.getValue()} />,
      }),
      columnHelper.accessor("monthly_change_pct", {
        header: "前月比",
        cell: (info) => <ChangePctCell value={info.getValue()} />,
      }),
      columnHelper.display({
        id: "favorite",
        header: "★",
        cell: ({ row }) => (
          <FavoriteStar ipId={row.original.ip_id} isFavorite={favSet.has(row.original.ip_id)} />
        ),
      }),
    ],
    [favSet]
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="検索"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-1 text-sm">
          カテゴリ
          <select
            aria-label="カテゴリ"
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">全て</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1 text-sm cursor-pointer">
          <input
            type="checkbox"
            aria-label="★のみ表示"
            checked={favoritesOnly}
            onChange={(e) => setFavoritesOnly(e.target.checked)}
          />
          ★のみ表示
        </label>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} / {data.length} 件</span>
      </div>
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    className="px-3 py-2 text-left font-medium text-muted-foreground cursor-pointer select-none"
                    onClick={h.column.getToggleSortingHandler()}
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {{ asc: " ▲", desc: " ▼" }[h.column.getIsSorted() as string] ?? ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isFav = favSet.has(row.original.ip_id);
              return (
                <tr
                  key={row.id}
                  className={cn(
                    "border-t cursor-pointer hover:bg-muted/30",
                    isFav && "bg-yellow-50"
                  )}
                  onClick={() => navigate(`/ips/${row.original.ip_id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
